import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Colors } from "../constants/Colors";
import logo from "../assets/images/ArkilaLogo.png";
import "../styles/sidebar.css";

const LG_BREAKPOINT = 992; // Bootstrap's lg

const NAV_GROUPS = [
  {
    items: [{ to: "/dashboard", icon: "bi-speedometer2", label: "Dashboard" }],
  },
  {
    heading: "Operations",
    items: [
      {
        key: "users",
        icon: "bi-people",
        label: "User Management",
        children: [
          { to: "/passengers", icon: "bi-person", label: "Passengers" },
          {
            key: "operators",
            icon: "bi-tools",
            label: "Operators",
            children: [
              { to: "/operators/operators", icon: "bi-people", label: "All Operators" },
              { to: "/operators/jeepney", icon: "bi-truck", label: "Jeepney Management" },
              { to: "/operators/drivers", icon: "bi-person-badge", label: "Driver Management" },
            ],
          },
        ],
      },
      { to: "/bookings", icon: "bi-calendar-check", label: "Bookings" },
      {
        key: "payments",
        icon: "bi-credit-card",
        label: "Payments",
        children: [
          { to: "/wallet/wallet", icon: "bi bi-wallet2", label: "Wallet" },
          { to: "/payments/payouts", icon: "bi-cash-coin", label: "Payout" },
          { to: "/payments-commission", icon: "bi-percent", label: "Commissions" },
          { to: "/payments-history", icon: "bi-clock-history", label: "Payment History" },
        ],
      },
      { to: "/location", icon: "bi-geo-alt", label: "Location & Tracking" },
    ],
  },
  {
    heading: "Insights",
    items: [
      { to: "/ratings", icon: "bi-star", label: "Rating & Feedback" },
      { to: "/audit/audit-logs", icon: "bi-shield-lock", label: "Audit Logs" },
    ],
  },
  {
    items: [{ to: "/settings/settings", icon: "bi-gear", label: "Settings" }],
  },
];

/**
 * Recursively checks whether a nav item (or any of its descendants)
 * matches the current route, so parent accordions auto-expand on load
 * or on direct navigation (e.g. refresh, deep link, browser back).
 */
const containsActivePath = (item, pathname) => {
  if (item.to) return pathname.startsWith(item.to);
  if (item.children) return item.children.some((c) => containsActivePath(c, pathname));
  return false;
};

/** Keeps items whose label matches, or that have a matching descendant. */
const filterItems = (items, q) =>
  items.reduce((acc, item) => {
    if (item.children) {
      if (item.label.toLowerCase().includes(q)) acc.push(item);
      else {
        const kids = filterItems(item.children, q);
        if (kids.length) acc.push({ ...item, children: kids });
      }
    } else if (item.label.toLowerCase().includes(q)) acc.push(item);
    return acc;
  }, []);

const collectKeys = (items, set = new Set()) => {
  items.forEach((item) => {
    if (item.children) {
      set.add(item.key);
      collectKeys(item.children, set);
    }
  });
  return set;
};

const Sidebar = ({ isOpen, toggleSidebar, onLogout }) => {
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [entering, setEntering] = useState(true);
  const didInit = useRef(false);

  /* ── One-time staggered entrance ── */
  useEffect(() => {
    const t = setTimeout(() => setEntering(false), 1100);
    return () => clearTimeout(t);
  }, []);

  const q = query.trim().toLowerCase();
  const groups = q
    ? NAV_GROUPS.map((g) => ({ ...g, items: filterItems(g.items, q) })).filter(
        (g) => g.items.length
      )
    : NAV_GROUPS;
  // while searching, open every branch that has a match
  const effectiveOpenKeys = q ? collectKeys(groups.flatMap((g) => g.items)) : openKeys;

  /* ── Expand whichever accordion(s) contain the active route ── */
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    const next = new Set();
    const walk = (items) => {
      items.forEach((item) => {
        if (item.children && containsActivePath(item, location.pathname)) {
          next.add(item.key);
          walk(item.children);
        }
      });
    };
    NAV_GROUPS.forEach((group) => walk(group.items));
    setOpenKeys(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Auto‑close on route change (mobile only) ── */
  useEffect(() => {
    if (isOpen && window.innerWidth < LG_BREAKPOINT) {
      toggleSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleKey = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="sidebar-overlay position-fixed top-0 start-0 w-100 h-100 d-lg-none"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={`sidebar d-flex flex-column ${isOpen ? "sidebar-open" : "sidebar-closed"}`}
        style={{
          background: `linear-gradient(160deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END})`,
        }}
        aria-label="Main navigation"
      >
        {/* ── Header ── */}
        <div className="sidebar-header d-flex align-items-center">
          <img src={logo} alt="Arkila" className="sidebar-logo" />
          <button
            className="sidebar-close-btn d-lg-none"
            onClick={toggleSidebar}
            aria-label="Close menu"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* ── Search ── */}
        <div className="sidebar-search">
          <i className="bi bi-search" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setQuery("")}
            placeholder="Search menu"
            aria-label="Search menu"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              className="sidebar-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              <i className="bi bi-x" />
            </button>
          )}
        </div>

        {/* ── Navigation ── */}
        <nav className={`sidebar-nav flex-grow-1${entering ? " entering" : ""}`}>
          {q && groups.length === 0 && (
            <div className="sidebar-empty">
              <i className="bi bi-compass" />
              <p>No pages match “{query.trim()}”.</p>
              <button type="button" onClick={() => setQuery("")}>
                Clear search
              </button>
            </div>
          )}
          {groups.map((group, i) => (
            <div className="sidebar-group" key={group.heading || `group-${i}`}>
              {group.heading && <div className="sidebar-group-label">{group.heading}</div>}
              {group.items.map((item) =>
                item.children ? (
                  <NavBranch
                    key={item.key}
                    item={item}
                    depth={0}
                    openKeys={effectiveOpenKeys}
                    onToggle={toggleKey}
                    pathname={location.pathname}
                  />
                ) : (
                  <NavLeaf key={item.to} item={item} depth={0} />
                )
              )}
            </div>
          ))}
        </nav>

        {/* ── Footer ── */}
        <div className="sidebar-footer">
          <div className="sidebar-avatar">
            <i className="bi bi-shield-check" />
          </div>
          <div className="sidebar-footer-text">
            <span className="sidebar-footer-title">Arkila Admin</span>
            <span className="sidebar-footer-sub">Administrator</span>
          </div>
          {onLogout && (
            <button
              type="button"
              className="sidebar-logout"
              onClick={onLogout}
              aria-label="Log out"
              title="Log out"
            >
              <i className="bi bi-box-arrow-right" />
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

/* ────────────────────── Helper Components ────────────────────── */

const NavLeaf = ({ item, depth }) => (
  <NavLink
    to={item.to}
    className={({ isActive }) => `sidebar-link${isActive ? " active" : ""}`}
    style={{ "--depth": depth }}
  >
    <i className={`bi ${item.icon} sidebar-icon`} />
    <span className="sidebar-label">{item.label}</span>
  </NavLink>
);

const NavBranch = ({ item, depth, openKeys, onToggle, pathname }) => {
  const isOpen = openKeys.has(item.key);
  const isActiveBranch = containsActivePath(item, pathname);

  return (
    <div className="sidebar-branch">
      <button
        type="button"
        className={`sidebar-link sidebar-toggle${isActiveBranch ? " active-parent" : ""}`}
        style={{ "--depth": depth }}
        onClick={() => onToggle(item.key)}
        aria-expanded={isOpen}
      >
        <i className={`bi ${item.icon} sidebar-icon`} />
        <span className="sidebar-label flex-grow-1">{item.label}</span>
        <i className={`bi bi-chevron-right sidebar-chevron${isOpen ? " open" : ""}`} />
      </button>

      <div className={`sidebar-collapse${isOpen ? " expanded" : ""}`}>
        <div className="sidebar-collapse-inner">
          {item.children.map((child) =>
            child.children ? (
              <NavBranch
                key={child.key}
                item={child}
                depth={depth + 1}
                openKeys={openKeys}
                onToggle={onToggle}
                pathname={pathname}
              />
            ) : (
              <NavLeaf key={child.to} item={child} depth={depth + 1} />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;